import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Button, Modal, CircularProgress, Chip } from "@mui/material";
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getHouseCostHistory } from '../../../api_calls/api_calls_houses';
import HomeIcon from '@mui/icons-material/Home';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BarChartIcon from '@mui/icons-material/BarChart';
import BillBreakdownModal from './BillBreakdownModal';
import EditHouseModal from './EditHouseModal';

function HouseMonitoringWelcomeMsg({ id, houseAddress, houseName, currentCost, costData, monthInsights, averageCost, showCost = true }) {
    const [open, setOpen] = useState(false);
    const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
    const [editHouseModalOpen, setEditHouseModalOpen] = useState(false);
    const [chartLoaded, setChartLoaded] = useState(false);
    const [costHistory, setCostHistory] = useState([]);
    const [costHistoryLoading, setCostHistoryLoading] = useState(true);
    const theme = useTheme();

    useEffect(() => {
        // Ensure costData is fully loaded before rendering the chart
        if (costData && costData.labels && costData.labels.length > 0) {
            setChartLoaded(true);
        }
    }, [costData]);

    useEffect(() => {
        // Fetch cost history for the house
        setCostHistoryLoading(true);
        getHouseCostHistory(id)
            .then(data => {
                // Transform API response to array of { month, cost }
                let arr = [];
                if (data && Array.isArray(data.costs) && Array.isArray(data.labels)) {
                    arr = data.labels.map((label, i) => ({ month: label, cost: data.costs[i] }));
                    // Sort descending by month (YYYY-MM)
                    arr.sort((a, b) => b.month.localeCompare(a.month));
                }
                setCostHistory(arr);
                setCostHistoryLoading(false);
            })
            .catch(() => setCostHistoryLoading(false));
    }, [id]);

    // Get current billing period (month/year)
    const currentDate = new Date();
    const billingPeriod = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    const currentDay = currentDate.getDate(); // Day of the month (1-31)

    // Calculate percentage change using mobile app formula:
    // ((totalCost / currentDay - averageCost / 30) / (averageCost / 30) * 100)
    const costPercentageChange = averageCost && currentCost && currentDay
        ? (((parseFloat(currentCost) / currentDay - parseFloat(averageCost) / 30) / (parseFloat(averageCost) / 30)) * 100).toFixed(1)
        : null;

    // Get consumption from monthInsights
    const consumption = monthInsights?.data?.current || 0;

    const handleEditClick = () => {
        setEditHouseModalOpen(true);
    };

    const handleClose = () => setOpen(false);

    const chartOptions = {
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                return params.value.toFixed(2) + " €";
            }
        },
        xAxis: {
            type: 'category',
            data: costData?.labels || []
        },
        yAxis: {
            type: 'value'
        },
        series: [
            {
                data: costData?.costs || [],
                type: 'bar'
            }
        ]
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    px: 3,
                    py: showCost ? '20px' : '12px',
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    width: '100%',
                    minHeight: showCost ? 700 : undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    mb: showCost ? 2 : 0
                }}
            >
                <Box sx={{ mb: 1 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(90deg, #1976D2 0%, #40C4FF 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <HomeIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                        Welcome home!
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Glad to see you back! Here’s your house overview.
                    </Typography>
                </Box>
                {/* House details and edit button row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                            {houseName}
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                            {houseAddress}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleEditClick}
                        startIcon={<EditNoteIcon sx={{ fontSize: 20 }} />}
                        sx={{
                            py: 1,
                            px: 2,
                            flexGrow: 1,
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 16,
                            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            color: 'white',
                            boxShadow: 'none',
                            textTransform: 'none',
                            minWidth: 0,
                            width: 'auto',
                            ml: 'auto',
                            '&:hover': {
                                background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Edit house
                    </Button>
                </Box>
                {showCost && (
                    <Grid container spacing={2} sx={{ mt: 'auto' }}>
                        <Grid item xs={12}>
                            <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
                                {/* Blue card with billing information */}
                                <Box
                                    sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                        borderRadius: '25px',
                                        padding: '24px',
                                        color: 'white',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Top section */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white' }}>
                                            Current Billing Period
                                        </Typography>
                                        <Chip
                                            label={billingPeriod}
                                            sx={{
                                                backgroundColor: theme.palette.primary.light,
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    </Box>

                                    {/* Middle section */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {/* Left: Electricity Cost */}
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1 }}>
                                                Electricity Cost
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                                                    €{currentCost}
                                                </Typography>
                                                {costPercentageChange && (
                                                    <Chip
                                                        label={`${costPercentageChange > 0 ? '+' : ''}${costPercentageChange}%`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: parseFloat(costPercentageChange) < 0 ? '#4caf50' : '#f44336',
                                                            color: 'white',
                                                            fontWeight: 700,
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>

                                        {/* Right: Consumption */}
                                        <Grid item xs={6}>
                                            <Box
                                                sx={{
                                                    backgroundColor: theme.palette.primary.light,
                                                    borderRadius: '20px',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: '80px',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <BoltIcon sx={{ color: '#FFD700', fontSize: 28 }} />
                                                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                                                        {consumption.toFixed(2)} kWh
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                    consumed so far
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Bottom section: View detailed bill breakdown */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: '16px', pt: '32px', pb: '12px',
                                        backgroundColor: theme.palette.common.white,
                                        borderRadius: '0 0 25px 25px',
                                        cursor: 'pointer',
                                        mt: '-20px',
                                        '&:hover': {
                                            backgroundColor: theme.palette.grey[50],
                                        },
                                    }}
                                    onClick={() => setBreakdownModalOpen(true)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BarChartIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                                            View detailed bill breakdown
                                        </Typography>
                                    </Box>
                                    <ArrowForwardIosIcon sx={{ color: theme.palette.grey[600], fontSize: 16 }} />
                                </Box>
                                {/* Cost History Chart (placeholder) */}
                                <Box sx={{ width: '100%', my: 1 }}>
                                    {/* Insert cost history chart component here, e.g. <CostHistoryChart data={costHistory} /> */}
                                </Box>
                                {/* Cost History List */}
                                <Paper elevation={0} sx={{
                                    width: '100%',
                                    background: theme.palette.grey[100],
                                    borderRadius: 3,
                                    p: 2,
                                    mt: 2,
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    '::-webkit-scrollbar': {
                                        width: 6,
                                        background: theme.palette.grey[200],
                                        borderRadius: 3,
                                    },
                                    '::-webkit-scrollbar-thumb': {
                                        background: theme.palette.primary.light,
                                        borderRadius: 3,
                                    },
                                }}>
                                    <Typography variant="h6" className='fancyText' fontWeight={700} sx={{ mb: 2 }}>
                                        Cost History
                                    </Typography>
                                    {costHistoryLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : (
                                        costHistory.map((item, idx) => {
                                            // Format month label
                                            const date = new Date(item.month + '-01');
                                            const monthLabel = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                                            return (
                                                <Box key={item.month} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1.5, borderRadius: 2, background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                                                    <CalendarMonthIcon sx={{ color: theme.palette.primary.main, fontSize: 32, mr: 2 }} />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle1" fontWeight={600}>{monthLabel}</Typography>
                                                        <Typography variant="caption" color="text.secondary">Total monthly electricity cost</Typography>
                                                    </Box>
                                                    <Typography variant="h6" fontWeight={700} sx={{ minWidth: 70, textAlign: 'right' }}>
                                                        €{item.cost?.toFixed(2) ?? '-'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Paper>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Paper >

            <Modal open={open} onClose={handleClose}>
                <Box style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    width: '50%',
                    padding: '20px',
                    borderRadius: '10px',
                }}>
                    <Typography variant="h6" gutterBottom>
                        Previous Months' Costs
                    </Typography>

                    {/* Show loading spinner if chart data isn't loaded */}
                    {!chartLoaded ? (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <ReactECharts option={chartOptions} />
                    )}

                    <Button onClick={handleClose} className={'fancyButton'} variant="contained" sx={{ mt: 2 }}>
                        Close
                    </Button>
                </Box>
            </Modal>

            <BillBreakdownModal
                open={breakdownModalOpen}
                onClose={() => setBreakdownModalOpen(false)}
                houseId={id}
            />
            <EditHouseModal
                open={editHouseModalOpen}
                onClose={() => setEditHouseModalOpen(false)}
                houseId={id}
            />
        </>
    );
}

export default HouseMonitoringWelcomeMsg;
